import React, { useEffect, useMemo, useState } from 'react';
import { exportToCSV } from '../../utils/exportData';
import useDaywiseByStationwiseGroup from '../../hooks/useDaywiseByStationwiseGroup';
import { formatINR } from "../../utils/formatINR";

const DateWiseStationData = ({ stationId, range }) => {
  const { data, isLoading, error } = useDaywiseByStationwiseGroup(range, stationId);
  const [localSessions, setLocalSessions] = useState([]);

  useEffect(() => {
    if (Array.isArray(data)) {
      const filtered = data
        .filter(s => s.StationID === String(stationId))
        .map(s => {
          const rawDate = s.SessionDate ? s.SessionDate.split("T")[0] : "";
          const formattedDate = rawDate
            ? new Date(rawDate).toLocaleDateString("en-GB", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })
            : "";

          return {
            date: formattedDate,
            rawDate: rawDate, // keep raw date for sorting
            total_cost: s.TotalCost || 0,
            gst_amount: s.TotalGSTAmount || 0,
            final_cost: s.TotalFinalCost || 0,
            energy_kwh: s.TotalEnergyKWh || 0,
            duration_minutes: (s.TotalDurationSeconds || 0) / 60,
            sessions_count: s.TotalSessions || 0,
          };
        });
      setLocalSessions(filtered);
    }
  }, [data, stationId]);

  const daywiseData = useMemo(() => {
    return [...localSessions].sort(
      (a, b) => new Date(b.rawDate) - new Date(a.rawDate) // sort desc
    );
  }, [localSessions]);

  const handleExport = () => {
    if (!daywiseData.length) return alert("No data to export");
    exportToCSV(daywiseData, `charging_sessions_daywise_${stationId}`);
  };

  if (isLoading) return <div className="p-4 text-sm text-gray-600">Loading daywise data...</div>;
  if (error) return <div className="p-4 text-sm text-red-600">Error loading data</div>;

  return (
    <div className="p-6">
      {/* Header */}
      <header className="mb-6 flex items-center justify-between">
        <h1 className="text-lg font-semibold text-gray-900">
          Daywise Charging Summary For Station {stationId}
        </h1>
        <button
          onClick={handleExport}
          className="bg-green-600 text-white text-sm font-medium px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
        >
          Export CSV
        </button>
      </header>


      {/* Table */}
      {daywiseData.length === 0 ? (
        <p className="text-sm text-gray-600 leading-relaxed">
          No data available for the selected range.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border border-gray-200">
            <thead>
              <tr className="bg-gray-50">
                {[
                  "Date",
                  "Sessions",
                  "Total Cost",
                  "GST",
                  "Final Cost",
                  "Energy (kWh)",
                  "Duration (min)",
                ].map((h, i) => (
                  <th
                    key={i}
                    className="border-b border-gray-200 px-4 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wide text-right first:text-left"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {daywiseData.map((d, idx) => (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-sm text-gray-800">{d.date}</td>
                  <td className="px-4 py-2 text-sm text-gray-800 text-right">{d.sessions_count}</td>
                  <td className="px-4 py-2 text-sm text-gray-800 text-right">{formatINR(d.total_cost)}</td>
                  <td className="px-4 py-2 text-sm text-gray-800 text-right">{formatINR(d.gst_amount)}</td>
                  <td className="px-4 py-2 text-sm text-gray-800 text-right">{formatINR(d.final_cost)}</td>
                  <td className="px-4 py-2 text-sm text-gray-800 text-right">{d.energy_kwh.toFixed(2)}</td>
                  <td className="px-4 py-2 text-sm text-gray-800 text-right">{d.duration_minutes.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DateWiseStationData;
